import React, { useState, useRef, useEffect } from "react";
import { musicLibrary, Track } from "./music_data";
import useWindowDrag from "./use_window_drag";
import styles from "./styles.module.css";
import { trackEvent } from "./track_event";

export default WinampPlayer;

const spotifyArtists: Record<string, string> = {
   "MSN Angel": "https://open.spotify.com/artist/1oqTb7DPcGxO7PTOEMI9H1",
};

function WinampPlayer({ onClose }: { onClose: () => void }) {
   const [currentTrack, setCurrentTrack] = useState<{ track: Track; artist: string; album: string } | null>(null);
   const [isPlaying, setIsPlaying] = useState(false);
   const [progress, setProgress] = useState(0);
   const [duration, setDuration] = useState(0);
   const [lowQuality, setLowQuality] = useState(false);
   const audioRef = useRef<HTMLAudioElement | null>(null);
   const { dragStyle, onTitleBarPointerDown } = useWindowDrag();
   const intervalRef = useRef<number | null>(null);
   const playNextRef = useRef<() => void>(() => {});

   useEffect(() => {
      const audio = new Audio();
      audio.preload = "none";
      audioRef.current = audio;

      audio.addEventListener("ended", () => {
         setIsPlaying(false);
         setProgress(0);
         playNextRef.current();
      });

      const updateDuration = () => {
         const d = audio.duration;
         if (isFinite(d) && d > 0) setDuration(d);
      };
      audio.addEventListener("loadedmetadata", updateDuration);
      audio.addEventListener("durationchange", updateDuration);
      audio.addEventListener("canplay", updateDuration);

      return () => {
         audio.pause();
         audio.src = "";
         if (intervalRef.current) clearInterval(intervalRef.current);
      };
   }, []);

   useEffect(() => {
      if (isPlaying) {
         intervalRef.current = window.setInterval(() => {
            if (audioRef.current) {
               setProgress(audioRef.current.currentTime);
               const d = audioRef.current.duration;
               if (isFinite(d) && d > 0) setDuration(d);
            }
         }, 200);
      } else {
         if (intervalRef.current) clearInterval(intervalRef.current);
      }
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
   }, [isPlaying]);

   function getAudioPath(file: string, low: boolean): string {
      return low ? file.replace("/audio/", "/audio-32/") : file;
   }

   function playTrack(track: Track, artist: string, album: string) {
      const audio = audioRef.current;
      if (!audio) return;
      audio.src = getAudioPath(track.file, lowQuality);
      audio.play();
      setCurrentTrack({ track, artist, album });
      setIsPlaying(true);
      setProgress(0);
      trackEvent(`song: ${album} / ${track.name}`);
   }

   function toggleQuality() {
      const newVal = !lowQuality;
      setLowQuality(newVal);
      if (currentTrack && audioRef.current) {
         const time = audioRef.current.currentTime;
         const wasPlaying = isPlaying;
         const audio = audioRef.current;
         audio.src = getAudioPath(currentTrack.track.file, newVal);
         const onReady = () => {
            audio.removeEventListener("canplay", onReady);
            audio.currentTime = time;
            if (wasPlaying) audio.play();
         };
         audio.addEventListener("canplay", onReady);
         audio.load();
      }
   }

   function togglePlay() {
      const audio = audioRef.current;
      if (!audio) return;
      if (!currentTrack) {
         const all = getAllTracks();
         if (all.length > 0) playTrack(all[0].track, all[0].artist, all[0].album);
         return;
      }
      if (isPlaying) {
         audio.pause();
         setIsPlaying(false);
      } else {
         audio.play();
         setIsPlaying(true);
      }
   }

   function stop() {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setProgress(0);
   }

   function getAllTracks(): { track: Track; artist: string; album: string }[] {
      const all: { track: Track; artist: string; album: string }[] = [];
      for (const a of musicLibrary) {
         for (const alb of a.albums) {
            for (const t of alb.tracks) {
               all.push({ track: t, artist: a.artist, album: alb.name });
            }
         }
      }
      return all;
   }

   function playNext() {
      const all = getAllTracks();
      if (!currentTrack) { if (all.length > 0) playTrack(all[0].track, all[0].artist, all[0].album); return; }
      const idx = all.findIndex(t => t.track.file === currentTrack.track.file);
      const next = all[(idx + 1) % all.length];
      playTrack(next.track, next.artist, next.album);
   }

   playNextRef.current = playNext;

   function playPrev() {
      const all = getAllTracks();
      if (!currentTrack) return;
      const idx = all.findIndex(t => t.track.file === currentTrack.track.file);
      const prev = all[(idx - 1 + all.length) % all.length];
      playTrack(prev.track, prev.artist, prev.album);
   }

   function formatTime(s: number): string {
      if (!isFinite(s) || isNaN(s)) return "0:00";
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${sec.toString().padStart(2, "0")}`;
   }

   const displayText = currentTrack
      ? `${isPlaying ? "►" : "||"} ${currentTrack.artist} - ${currentTrack.track.name}`
      : "► windows2001 player";

   return (
      <div className={styles.winampWindow} onContextMenu={(e) => e.preventDefault()}>
         <div className={styles.winamp} style={dragStyle}>
            <div className={styles.winampTitleBar} onPointerDown={onTitleBarPointerDown}>
               <span>Winamp</span>
               <span className={styles.winampClose} onClick={onClose}>✕</span>
            </div>
            <div className={styles.winampDisplay}>
               <div>{displayText}</div>
               {currentTrack && (
                  <div style={{ fontSize: "13px", marginTop: "2px", color: "#00aa00" }}>
                     {formatTime(progress)} / {formatTime(duration)} — {currentTrack.album}
                  </div>
               )}
            </div>
            <div className={styles.winampSeek}>
               <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  value={progress}
                  step={0.1}
                  onChange={(e) => {
                     const time = parseFloat(e.target.value);
                     if (audioRef.current) {
                        audioRef.current.currentTime = time;
                        setProgress(time);
                     }
                  }}
                  className={styles.winampSlider}
               />
            </div>
            <div className={styles.winampControls}>
               <span
                  className={!lowQuality ? styles.winampKbpsBtnActive : styles.winampKbpsBtn}
                  onClick={() => { if (lowQuality) toggleQuality(); }}
               >192</span>
               <span
                  className={lowQuality ? styles.winampKbpsBtnActive : styles.winampKbpsBtn}
                  onClick={() => { if (!lowQuality) toggleQuality(); }}
               >32</span>
               <span className={styles.winampControlsSpacer} />
               <span className={styles.winampBtn} onClick={playPrev}>◄◄</span>
               <span className={styles.winampBtn} onClick={togglePlay}>{isPlaying ? "||" : "►"}</span>
               <span className={styles.winampBtn} onClick={stop}>■</span>
               <span className={styles.winampBtn} onClick={playNext}>►►</span>
            </div>
            <div className={styles.winampPlaylist}>
               {musicLibrary.map((artist) => (
                  <React.Fragment key={artist.artist}>
                     <div className={styles.winampArtist}>
                        {artist.artist}
                        {spotifyArtists[artist.artist] && (
                           <a
                              href={spotifyArtists[artist.artist]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: "8px", color: "#1DB954", textDecoration: "none", fontSize: "12px" }}
                           >
                              ● Spotify
                           </a>
                        )}
                     </div>
                     {artist.albums.map((album) => (
                        <React.Fragment key={album.name}>
                           <div className={styles.winampAlbum}>{album.name}</div>
                           {album.tracks.map((track, i) => (
                              <div
                                 key={i}
                                 className={styles.winampTrack}
                                 style={currentTrack?.track.file === track.file ? { backgroundColor: "#000080", color: "white" } : undefined}
                                 onClick={() => playTrack(track, artist.artist, album.name)}
                              >
                                 {i + 1}. {track.name}
                              </div>
                           ))}
                        </React.Fragment>
                     ))}
                  </React.Fragment>
               ))}
            </div>
         </div>
      </div>
   );
}
